import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | already | error
  const [message, setMessage] = useState("");
  const [resendEmail, setResendEmail] = useState(searchParams.get("email") || "");
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const { user, token } = useAuth();

  const handleResend = async () => {
    if (!resendEmail.trim() || resending) return;
    setResending(true);
    try {
      const res = await fetch(`${API}/auth/resend-verification-public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail.trim() }),
      });
      if (res.status === 429) {
        setResendMessage("Too many requests — please wait a minute and try again.");
        setResendSent(true);
        return;
      }
      const data = await res.json();
      setResendMessage(data.message || "If an unverified account exists with this email, a new verification link has been sent.");
      setResendSent(true);
    } catch (err) {
      setResendMessage("Something went wrong. Please try again in a moment.");
      setResendSent(true);
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const verifyToken = searchParams.get("token");
    if (!verifyToken) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    fetch(`${API}/auth/verify-email?token=${verifyToken}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.detail) {
          setStatus("error");
          setMessage(data.detail);
        } else if (data.already_verified) {
          setStatus("already");
          setMessage(data.message);
        } else {
          setStatus("success");
          setMessage(data.message);
          // Refresh user data if logged in
          if (token) {
            window.location.reload();
          }
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Something went wrong. Please try again.");
      });
  }, [searchParams, token]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar />

      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
        <Card className="bg-white/[0.03] border-white/10 p-10 max-w-md w-full text-center" data-testid="verify-email-card">
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-white/30 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Verifying your email...</h2>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Email Verified!</h2>
              <p className="text-white/60 mb-6">{message}</p>
              <Link to={user ? "/dashboard" : "/login"}>
                <Button className="bg-white text-black hover:bg-gray-200 font-semibold px-8" data-testid="verify-continue-btn">
                  {user ? "Go to Dashboard" : "Log In"}
                </Button>
              </Link>
            </>
          )}

          {status === "already" && (
            <>
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white/40" />
              </div>
              <h2 className="text-xl font-bold mb-2">Already Verified</h2>
              <p className="text-white/60 mb-6">{message}</p>
              <Link to={user ? "/dashboard" : "/login"}>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8">
                  {user ? "Go to Dashboard" : "Log In"}
                </Button>
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold mb-2">Verification Failed</h2>
              <p className="text-white/60 mb-6">{message}</p>

              {!resendSent ? (
                <div className="space-y-3 mb-4" data-testid="resend-verification-block">
                  <p className="text-sm text-white/50">
                    Check the email address below — if it&apos;s wrong, correct it and we&apos;ll send a new link.
                  </p>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 rounded-md bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-white/30"
                    data-testid="resend-verification-email"
                    disabled={resending}
                  />
                  <Button
                    onClick={handleResend}
                    disabled={resending || !resendEmail.trim()}
                    className="bg-[#FF6B6B] text-white hover:bg-[#FF6B6B]/90 font-semibold w-full"
                    data-testid="resend-verification-submit"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      "Send a new verification email"
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4 mb-4 text-sm text-green-300" data-testid="resend-verification-success">
                  {resendMessage}
                </div>
              )}

              <Link to={user ? "/dashboard" : "/login"}>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8">
                  {user ? "Go to Dashboard" : "Log In"}
                </Button>
              </Link>
            </>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}
